/** The launch band's edit-mode marks (plan §1): per node, the draft's unsaved changes in
 *  its blocks and how many of them the next import will undo (an import-forced field). */
import { useMemo } from 'react';

import { parseRecordKey, type RecordKey } from '@/features/records/draft/changeSet';
import { useDraft } from '@/features/records/draft/DraftProvider';
import { useRecordEditor } from '@/features/records/editor/RecordEditorProvider';
import type { StageMarks } from '@/features/bookings/record/views/SpineNav';

import { ALIAS_ENTITY, TAGCAT_ENTITY, TOUR_ENTITY } from '../catalog';
import type { ReadinessNode } from '../readiness';

/** The tour fields each node's blocks hold (the collection rows go by entity). */
const TOUR_PATH_NODES: Readonly<Record<string, ReadinessNode>> = {
  custom_id: 'mapping',
  'additional_custom_ids.order': 'mapping',
  launch_policy: 'policy',
  atag: 'access',
  tags: 'access',
  use_operator_defaults: 'tag-categories',
  'tag_categories.order': 'tag-categories',
  // Guest app: self-registration, chat and My Trips.
  allow_selfreg: 'guest-app',
  location_sharing: 'guest-app',
  mytrips_display: 'guest-app',
  has_group_chat: 'guest-app',
  exclude_from_chat: 'guest-app',
};

/** The band node an entry belongs to; null = none (Basics, Content, …). */
export function bandNodeOf(rec: RecordKey, path: string): ReadinessNode | null {
  const { entity } = parseRecordKey(rec);
  if (entity === ALIAS_ENTITY) return 'mapping';
  if (entity === TAGCAT_ENTITY) return 'tag-categories';
  if (entity === TOUR_ENTITY) return TOUR_PATH_NODES[path] ?? null;
  return null;
}

export function useBandMarks(): Partial<Record<ReadinessNode, StageMarks>> {
  const { state } = useDraft();
  const { fieldForPath, config } = useRecordEditor();
  return useMemo(() => {
    const out: Partial<Record<ReadinessNode, StageMarks>> = {};
    for (const [rec, paths] of Object.entries(state.entries)) {
      for (const path of Object.keys(paths)) {
        const node = bandNodeOf(rec, path);
        if (!node) continue;
        const m = (out[node] ??= { count: 0, reverts: 0 });
        m.count += 1;
        const field = fieldForPath(path, rec);
        if (field && config.verdictOf(rec, field, state)?.verdict === 'overwrites') m.reverts += 1;
      }
    }
    return out;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.version, fieldForPath, config]);
}
